import React from "react";
import logoBlack from "../assets/logo-black.png";

// Guidelines Data
const guidelines = [
  {
    category: "Introduction",
    content: [
      "Conquer Life is a privacy-focused platform dedicated to fostering positive connections in a safe, inclusive, and respectful environment. Users interact using usernames rather than real names. To nurture meaningful and equitable relationships, we hold our members accountable for their behavior toward each other. Our Community Guidelines are in place to protect our members and ensure a welcoming space for everyone. They clearly outline the types of content and behavior that are not allowed on our platform.",
      "Conquer Life employs both automated moderation tools and a dedicated team of human moderators to proactively review user-generated content, including profiles and messages. Users can report objectionable content or other users directly within the app at any time. All reports are reviewed promptly, and enforcement actions are taken as described in the Enforcement Philosophy section below. Users may also block other users and leave or end any conversation at any time. For support, contact us at info@conquerlife.io or through the in-app Feedback section (Menu → Settings → Feedback).",
    ],
  },
  {
    category: "Profile Guidelines",
    content: [
      {
        question: "Age",
        answer:
          "You must be at least 18 years old to join Conquer Life. Misrepresenting your age as under 18 is not allowed. We may take steps to verify your age, and users under 18 will be blocked from the platform.",
      },
      {
        question: "Username",
        answer:
          "Your username is how other users will identify you on the platform. We do not require real names—users interact using usernames to maintain privacy. We encourage creativity, but usernames must comply with our Community Guidelines. Avoid using your real name, and steer clear of any celebrity or fictional names, as well as words, symbols, or emojis that don't fit within these guidelines.",
      },
      {
        question: "Profile Color",
        answer:
          "You can choose a profile color that represents you. This is your chance to personalize your profile while maintaining your privacy.",
      },
      {
        question: "About / Bio",
        answer:
          "Your bio is a space to share more about yourself, but it should not contain any personally identifiable information such as your real name, email, or phone number. Keep it fun, interesting, and safe!",
      },
      {
        question: "Interests",
        answer:
          "You can add or edit your interests from a predefined list of categories, such as football, hockey, dancing, baking, cooking, beekeeping, and more. These interests help others understand your hobbies and passions without revealing personal details.",
      },
      {
        question: "Personality",
        answer:
          "We offer eight broad categories for you to define aspects of your personality: Sports, Music, Thoughts, Feelings, and Expressions, Illness, Traumas, Culture and Nations, Home Life, and Self-Characteristics.",
      },
    ],
  },
  {
    category: "Content and Conduct Guidelines",
    content: [
      {
        question: "Adult Content and Sexual Activity",
        answer:
          "We don't allow any nude, sexually explicit, or vulgar content in profiles. Commercial exchange or promotion of romantic or sexual content, services, or activities is strictly prohibited. Conquer Life is a space for meaningful connections, not for sexual content or transactions.",
      },
      {
        question: "Bullying and Abusive Conduct",
        answer:
          "Conquer Life is all about fostering kind and supportive connections. We do not tolerate any behavior or content that harasses, bullies, or targets individuals or groups. This includes belittling, insulting, or intimidating others; making unsolicited comments about someone's appearance; emotional abuse; blackmail; repeated unwanted contact; or promoting violence in any form.",
      },
      {
        question: "Child Protection",
        answer:
          "We maintain a zero-tolerance policy for any form of child exploitation or abuse. Content that sexualizes or endangers children, whether real or fictional, is strictly prohibited. This includes visual depictions or discussions of sexually explicit conduct involving minors. Uploading, sharing, or enticing others to share child abuse material is illegal and will result in immediate removal from the platform.",
      },
      {
        question: "Commercial and Promotional Activity",
        answer:
          "Conquer Life is not a marketplace. We do not permit the use of our platform for unsolicited commercial or promotional purposes. The focus here is on genuine connections, not business transactions.",
      },
      {
        question: "Controlled Substances and Goods",
        answer:
          "Using Conquer Life to buy, sell, or distribute illegal drugs or misuse controlled substances is strictly forbidden. This includes e-cigarettes, marijuana, drug paraphernalia, or the misuse of legal substances like prescription drugs, tobacco, or alcohol.",
      },
      {
        question: "Dangerous Organizations and Individuals",
        answer:
          "We do not allow any organizations or individuals that support or glorify violence, terrorism, or dangerous missions to have a presence on Conquer Life. Safety and peace of mind are our top priorities.",
      },
      {
        question: "Identity-Based Hate",
        answer:
          "Conquer Life is committed to being a diverse and inclusive community. We prohibit any content or behavior that promotes hate, dehumanizes, or shows contempt towards marginalized or minoritized communities based on attributes such as race, ethnicity, nationality, caste, gender identity, sexual orientation, disability, or religion.",
      },
      {
        question: "Inauthentic Profiles",
        answer:
          "We value authenticity and expect all our members to represent themselves truthfully. Impersonation or misrepresentation, including creating fake profiles or falsely stating information about yourself, is not allowed. Your profile should be a true reflection of who you are.",
      },
      {
        question: "Misinformation",
        answer:
          "Sharing false or misleading content that could cause harm or negatively impact safety is prohibited. This includes content that contradicts reputable health guidance, misinformation about civic processes, or unproven and dangerous conspiracy theories.",
      },
      {
        question: "Physical and Sexual Violence",
        answer:
          "We have a zero-tolerance policy for any content or behavior that threatens or commits acts of physical or sexual violence. This includes stalking, exploitation, human trafficking, and sexual assault. Conquer Life is a safe space, and such actions will not be tolerated.",
      },
      {
        question: "Scams and Fraud",
        answer:
          "We do not permit any activity intended to defraud or manipulate members. This includes requests for financial support under false pretenses, lying for financial gain, or faking romantic intentions to deceive others.",
      },
      {
        question: "Sexual Harassment",
        answer:
          "Sexual harassment, including non-physical, unwanted, and unwelcome sexual behaviors, is not tolerated on Conquer Life. This includes sending unsolicited explicit images, making unwanted sexual comments, or threatening to share intimate images without consent.",
      },
      {
        question: "Sharing Personal Information",
        answer:
          "Sharing your own or another person's personal information is strictly prohibited. This includes phone numbers, home or work addresses, email addresses, financial information, government-issued identification numbers, or any other data that could be used to identify or locate someone outside of the platform. Protect your privacy and the privacy of others at all times.",
      },
      {
        question: "Unwanted Sexual Messages",
        answer:
          "Sending unsolicited sexual messages, images, or advances is not tolerated. All interactions on Conquer Life must be consensual and respectful. Repeatedly sending unwanted sexual content to another user after being asked to stop will result in enforcement action, up to and including permanent removal from the platform.",
      },
      {
        question: "Spam",
        answer:
          "Unwanted or irrelevant content, sent in bulk or at high frequency, is not allowed. This includes misleading links, creating excessive accounts to disrupt the community, or having multiple profiles to engage in unwanted interactions.",
      },
      {
        question: "Suicide and Self-Harm",
        answer:
          "We care deeply about the mental health and well-being of our members. While we support sharing personal experiences with mental health challenges in a safe way, we do not allow content that promotes or glorifies suicide, self-harm, or disordered eating.",
      },
      {
        question: "Violent and Graphic Content",
        answer:
          "Violent, graphic, or gory content has no place on Conquer Life. This includes descriptions of violence, images of blood or injury, and photos depicting weapons (except when shown on uniformed law enforcement or military personnel).",
      },
    ],
  },
  {
    category: "Platform Manipulation",
    content: [
      "At Conquer Life, we are committed to fostering a community based on genuine connections. Any attempts to artificially influence interactions, connections, conversations, or engagement through the use of automation, scripts, or any other manipulative techniques are strictly prohibited. We value authentic relationships and expect all members to engage honestly and transparently.",
    ],
  },
  {
    category: "Safety Reporting",
    content: [
      "Safety is a top priority at Conquer Life. We utilize a combination of human moderators and automated systems to proactively monitor and review accounts and interactions for any content that may violate our Community Guidelines, breach our Terms & Conditions, or pose any harm. Reports submitted by users are reviewed in a timely manner by our moderation team, and appropriate enforcement actions are taken as described in the Enforcement Philosophy section.",
      "Our members play a vital role in maintaining the safety of Conquer Life by reporting content or behavior that may violate our Community Guidelines. If you encounter anything that makes you feel uncomfortable or unsafe, we strongly encourage you to Block and Report the member directly within the app. You may also end or leave any conversation at any time. For more details on what happens when you report something to Conquer Life, please refer to our FAQ page.",
      "However, it's important to remember that simply disagreeing with or disliking a member or their content does not always warrant a report. We may take action against any member found to be deliberately making false or inappropriate reports against others based solely on their protected attributes. This includes reporting members solely based on their gender identity or expression, or repeatedly submitting false reports of bad behavior.",
    ],
  },
  {
    category: "Enforcement Philosophy",
    content: [
      "All members must comply with the platform rules described and referenced in our Community Guidelines. If you behave in a way that goes against Conquer Life's Community Guidelines, values, or otherwise act in any way we believe to be potentially harmful to Conquer Life or its members, we may take a range of actions on your account. When determining the penalty for violating our Community Guidelines, we consider a number of factors including the severity of the violation, whether it is a repeated offense, and the potential harm to other users.",
      "Violations of these Community Guidelines may result in one or more of the following enforcement actions: a formal warning; temporary restriction of account features; removal of offending content; temporary suspension of the account; or a permanent ban from all Conquer Life apps. Severe violations—including any content involving the exploitation of minors, credible threats of violence, or repeated harassment—may result in an immediate permanent ban without prior warning.",
      "When necessary, we may also cooperate with law enforcement to assist in potential criminal investigations related to member conduct. Your treatment towards others outside of the Conquer Life app can also result in action against your account. If we're made aware of harm between members via text message or direct messaging platforms, or relevant alleged criminal or harmful conduct committed in your past or outside of Conquer Life, we may take action as if it happened on our platform.",
      "If you believe we've made a mistake in taking action on your account or content, you can always contact us at info@conquerlife.io or through the in-app Feedback section.",
    ],
  },
];

const renderAnswer = (text) => {
  const urlPatterns = [
    {
      pattern: /Conquer Life App/g,
      url: process.env.REACT_APP_CONQUER_LIFE_HOME_URL,
    },
    {
      pattern: /FAQ/g,
      url: process.env.REACT_APP_CONQUER_LIFE_FAQ_URL,
    },
    {
      pattern: /Terms & Conditions/g,
      url: process.env.REACT_APP_CONQUER_LIFE_TERMS_AND_CONDITIONS_URL,
    },
  ];

  let parts = [text]; // Start with the entire text as a single part

  urlPatterns.forEach(({ pattern, url }) => {
    parts = parts.flatMap((part) =>
      typeof part === "string"
        ? part.split(pattern).flatMap((splitPart, index, array) =>
            index < array.length - 1
              ? [
                  splitPart,
                  <a
                    key={splitPart + index}
                    href={url}
                    style={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pattern.source.replace(/\\/g, "")}
                  </a>,
                ]
              : splitPart,
          )
        : [part],
    );
  });

  return <div style={styles.answer} className="text-body">{parts}</div>;
};

const CommunityGuidelines = () => {
  return (
    <div style={styles.container}>
      {/* ✅ HEADER */}
      <div style={styles.header}>
        <img
          src={logoBlack}
          alt="Conquer Life Logo"
          style={styles.logo}
        />
        <h1 style={styles.headerTitle} className="text-big-heading">CONQUER LIFE</h1>
      </div>
      <div style={styles.headingContainer}>
        <h1 style={styles.heading} className="text-big-heading">Community Guidelines</h1>
      </div>
      <div style={styles.scrollView}>
        {guidelines.map((section, index) => (
          <div key={index} style={styles.section}>
            <h2 style={styles.categoryTitle} className="text-heading">{section.category}</h2>
            {section.content.map((item, idx) => (
              <div key={idx} style={styles.content} className="text-body">
                {typeof item === "string" ? (
                  <div style={styles.text} className="text-body">{renderAnswer(item)}</div>
                ) : (
                  <>
                    <h3 style={styles.question} className="text-sub-heading">{item.question}</h3>
                    {renderAnswer(item.answer)}
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommunityGuidelines;

// Styles Object (similar to React Native StyleSheet)
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#ffffff",
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid black",
    height: "60px",
    padding: "0 15px",
    boxSizing: "border-box",
  },
  headerTitle: {
    fontSize: "26px",
    fontWeight: "bold",
    color: "black",
    margin: 0,
  },
  logo: {
    width: "40px",
    height: "40px",
    marginRight: "15px",
  },
  scrollView: {
    marginRight: "30px",
    marginLeft: "30px",
    paddingBottom: "20px",
  },
  section: {
    marginBottom: "20px",
  },
  categoryTitle: {
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#333",
  },
  content: {
    marginBottom: "10px",
  },
  question: {
    fontWeight: "bold",
    marginBottom: "5px",
    marginTop: "0",
    color: "#333",
  },
  answer: {
    marginBottom: "10px",
  },
  text: {},
  headingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // marginTop: "25px",
    // marginBottom: "20px",
    padding: "2rem",
  },
  heading: {
    textAlign: "center",
    fontSize: "2.5rem",
    color: "#333",
    marginBottom: "2rem",
  },
  link: {
    color: "blue",
    textDecoration: "underline",
    cursor: "pointer",
  },
};
